/**
 * Frontend mirror of the language registry.
 *
 * SOURCE OF TRUTH: backend/utils/languages.js — the RAW table below is copied
 * from it verbatim. The frontend cannot import across the CRA module scope, so
 * the copy is guarded by src/utils/languages.test.js, which reads both files and
 * fails if they diverge. Edit the backend file, then run that test.
 *
 * Canonical code is ISO 639-2/T ('eng', 'deu', 'fas') — what the feed sends and
 * what every stored article already holds.
 *
 * Prefer server-provided names where they exist (a story card carries
 * `languageNames`); this exists for the paths that only have a raw code.
 */

const RAW = [
  ['English', 'eng', 'en', 'EN'],
  ['Spanish', 'spa', 'es', 'ES'],
  ['Chinese', 'zho', 'zh', 'ZH', 'chi', 'cmn', 'yue'],
  ['Hindi', 'hin', 'hi', null],
  ['French', 'fra', 'fr', 'FR', 'fre'],
  ['German', 'deu', 'de', 'DE', 'ger'],
  ['Arabic', 'ara', 'ar', 'AR'],
  ['Portuguese', 'por', 'pt', 'PT'],
  ['Italian', 'ita', 'it', 'IT'],
  ['Russian', 'rus', 'ru', 'RU'],
  ['Hebrew', 'heb', 'he', 'HE', 'iw'],
  ['Ukrainian', 'ukr', 'uk', 'UK'],
  ['Persian', 'fas', 'fa', null, 'per', 'pes', 'prs', 'farsi', 'dari'],
  ['Turkish', 'tur', 'tr', 'TR'],
  ['Greek', 'ell', 'el', 'EL', 'gre'],
  ['Norwegian', 'nor', 'no', 'NB', 'nb', 'nob', 'nn', 'nno', 'norwegian bokmal', 'norwegian bokmål'],
  ['Czech', 'ces', 'cs', 'CS', 'cze'],
  ['Dutch', 'nld', 'nl', 'NL', 'dut'],
  ['Afrikaans', 'afr', 'af', null],
  ['Albanian', 'sqi', 'sq', null, 'alb'],
  ['Amharic', 'amh', 'am', null],
  ['Armenian', 'hye', 'hy', null, 'arm'],
  ['Azerbaijani', 'aze', 'az', null],
  ['Basque', 'eus', 'eu', null, 'baq'],
  ['Belarusian', 'bel', 'be', null],
  ['Bengali', 'ben', 'bn', null],
  ['Bosnian', 'bos', 'bs', null],
  ['Bulgarian', 'bul', 'bg', 'BG'],
  ['Burmese', 'mya', 'my', null, 'bur'],
  ['Catalan', 'cat', 'ca', null],
  ['Cebuano', 'ceb', null, null],
  ['Croatian', 'hrv', 'hr', null],
  ['Danish', 'dan', 'da', 'DA'],
  ['Esperanto', 'epo', 'eo', null],
  ['Estonian', 'est', 'et', 'ET'],
  ['Tagalog', 'tgl', 'tl', null, 'fil', 'filipino'],
  ['Tajik', 'tgk', 'tg', null],
  ['Finnish', 'fin', 'fi', 'FI'],
  ['Galician', 'glg', 'gl', null],
  ['Georgian', 'kat', 'ka', null, 'geo'],
  ['Gujarati', 'guj', 'gu', null],
  ['Haitian Creole', 'hat', 'ht', null],
  ['Hausa', 'hau', 'ha', null],
  ['Hungarian', 'hun', 'hu', 'HU'],
  ['Icelandic', 'isl', 'is', null, 'ice'],
  ['Indonesian', 'ind', 'id', 'ID', 'in'],
  ['Ilocano', 'ilo', null, null],
  ['Irish', 'gle', 'ga', null],
  ['Japanese', 'jpn', 'ja', 'JA'],
  ['Javanese', 'jav', 'jv', null],
  ['Kannada', 'kan', 'kn', null],
  ['Kazakh', 'kaz', 'kk', null],
  ['Khmer', 'khm', 'km', null],
  ['Kinyarwanda', 'kin', 'rw', null],
  ['Korean', 'kor', 'ko', 'KO'],
  ['Kurdish', 'kur', 'ku', null],
  ['Lao', 'lao', 'lo', null],
  ['Latin', 'lat', 'la', null],
  ['Latvian', 'lav', 'lv', 'LV'],
  ['Luxembourgish', 'ltz', 'lb', null],
  ['Lithuanian', 'lit', 'lt', 'LT'],
  ['Macedonian', 'mkd', 'mk', null, 'mac'],
  ['Malay', 'msa', 'ms', null, 'may'],
  ['Malayalam', 'mal', 'ml', null],
  ['Maltese', 'mlt', 'mt', null],
  ['Marathi', 'mar', 'mr', null],
  ['Mongolian', 'mon', 'mn', null],
  ['Nepali', 'nep', 'ne', null],
  ['Pashto', 'pus', 'ps', null],
  ['Polish', 'pol', 'pl', 'PL'],
  ['Punjabi', 'pan', 'pa', null],
  ['Romanian', 'ron', 'ro', 'RO', 'rum', 'mol', 'mo'],
  ['Serbian', 'srp', 'sr', null],
  ['Sinhala', 'sin', 'si', null],
  ['Slovak', 'slk', 'sk', 'SK', 'slo'],
  ['Slovenian', 'slv', 'sl', 'SL'],
  ['Somali', 'som', 'so', null],
  ['Swahili', 'swa', 'sw', null],
  ['Swedish', 'swe', 'sv', 'SV'],
  ['Tamil', 'tam', 'ta', null],
  ['Telugu', 'tel', 'te', null],
  ['Thai', 'tha', 'th', 'TH'],
  ['Turkmen', 'tuk', 'tk', null],
  ['Urdu', 'urd', 'ur', null],
  ['Uzbek', 'uzb', 'uz', null],
  ['Vietnamese', 'vie', 'vi', 'VI'],
  ['Waray-Waray', 'war', null, null],
  ['Welsh', 'cym', 'cy', null, 'wel'],
  ['Yiddish', 'yid', 'yi', null],
  ['Yoruba', 'yor', 'yo', null],
  ['Zulu', 'zul', 'zu', null],
];

const BY_ALIAS = new Map();
const BY_CODE = new Map();
const ENTRIES = RAW.map(([name, code, iso2, deepL, ...aliases]) => ({ name, code, iso2, deepL: deepL || null, aliases }));
for (const { name, code, iso2, aliases } of ENTRIES) {
    BY_CODE.set(code, name);
    for (const key of [code, iso2, name.toLowerCase(), ...aliases]) {
        if (key) BY_ALIAS.set(key, code);
    }
}
const BY_CANONICAL = new Map(ENTRIES.map((l) => [l.code, l]));

/** Reduce any spelling of a language ('per', 'fa', 'fa-IR') to its canonical code. */
export function normalizeLangCode(raw) {
    if (raw === null || raw === undefined) return null;
    const key = String(raw).trim().toLowerCase().replace(/_/g, '-');
    if (!key || key === 'unknown' || key === 'und') return null;
    const exact = BY_ALIAS.get(key);
    if (exact) return exact;
    const base = key.split('-')[0];
    return BY_ALIAS.get(base) || base;
}

/** Human-readable name for any spelling of a language; falls back to the code. */
export function getLanguageName(raw) {
    const code = normalizeLangCode(raw);
    if (!code) return '';
    return BY_CODE.get(code) || code;
}

/** ISO 639-1 for any spelling — what the translation endpoints expect. */
export function toIso2(raw) {
    const entry = BY_CANONICAL.get(normalizeLangCode(raw));
    return entry ? entry.iso2 : null;
}

/** True for any spelling of the same language. */
export function isSameLanguage(a, b) {
    const ca = normalizeLangCode(a);
    const cb = normalizeLangCode(b);
    return Boolean(ca && cb && ca === cb);
}

export const isEnglish = (code) => isSameLanguage(code, 'eng');

const RTL_CODES = new Set(['heb', 'ara', 'fas', 'urd', 'pus', 'kur']);
export const isRTLLanguage = (code) => RTL_CODES.has(normalizeLangCode(code));

/** Every language the app can FILTER by — the source corpus, in display order. */
export const LANGUAGE_NAMES = ENTRIES.map((l) => l.name);

/**
 * Every language the app can DELIVER in — i.e. translate a brief into.
 * A language qualifies exactly when it has a DeepL target code, mirroring
 * backend/utils/outputLanguages.js. Offering a source language here would let a
 * user pick one we cannot translate into and silently receive English.
 */
export const OUTPUT_LANGUAGE_NAMES = ENTRIES
    .filter((l) => l.deepL)
    .map((l) => l.name)
    .sort((a, b) => (a === 'English' ? -1 : b === 'English' ? 1 : a.localeCompare(b)));

/** Can a brief actually be translated into this language? */
export const isTranslatable = (raw) => {
    const entry = BY_CANONICAL.get(normalizeLangCode(raw));
    return Boolean(entry && entry.deepL);
};

/**
 * Coerce any spelling of a language ('de', 'German', 'german') into the exact
 * string used as an <option> value in the brief Language select, or null when
 * the app cannot deliver in that language. Setting the select to a value that
 * is not in OUTPUT_LANGUAGE_NAMES would render it blank, so callers that copy a
 * language in from elsewhere (e.g. a bucket's targetLanguage) must go through here.
 */
export function toOutputLanguageName(raw) {
    const entry = BY_CANONICAL.get(normalizeLangCode(raw));
    if (!entry || !entry.deepL) return null;
    return entry.name;
}
